import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Event, parseLocalDate, formatDateForDisplay } from '../lib/supabase';
import EventForm from './EventForm';
import EventDetail from './EventDetail';
import './EventsList.css';

interface EventsListProps {
  events: Event[];
  onAddEvent: (event: Omit<Event, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onUpdateEvent: (id: string, updates: Partial<Event>) => Promise<void>;
  onDeleteEvent: (id: string) => Promise<void>;
}

const EventsList: React.FC<EventsListProps> = ({ events, onAddEvent, onUpdateEvent, onDeleteEvent }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [startInEditMode, setStartInEditMode] = useState(false);
  const scrollPositionRef = useRef<number>(0);
  
  // Update selectedEvent when events array changes (after save)
  useEffect(() => {
    if (selectedEvent?.id) {
      const updatedEvent = events.find(e => e.id === selectedEvent.id);
      if (updatedEvent) {
        setSelectedEvent(updatedEvent);
      }
    }
  }, [events, selectedEvent?.id]);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const dateA = parseLocalDate(a.start_date);
      const dateB = parseLocalDate(b.start_date);
      return dateA.getTime() - dateB.getTime();
    });
  }, [events]);

  const getPriorityBadge = (priority?: string) => {
    if (!priority) return <span className="priority-badge empty">—</span>;
    const className = priority.toLowerCase() === 'high' ? 'high' : 
                     priority.toLowerCase() === 'medium' ? 'medium' : 'low';
    return <span className={`priority-badge ${className}`}>{priority}</span>;
  };

  const handleRowClick = (event: Event) => {
    // Save current scroll position
    scrollPositionRef.current = window.scrollY || document.documentElement.scrollTop;
    setSelectedEvent(event);
    setStartInEditMode(false);
  };
  
  const handleEditClick = (e: React.MouseEvent, event: Event) => {
    e.stopPropagation();
    scrollPositionRef.current = window.scrollY || document.documentElement.scrollTop;
    setSelectedEvent(event);
    setStartInEditMode(true);
  };
  
  const handleCloseModal = () => {
    setSelectedEvent(null);
    // Restore scroll position after a brief delay to ensure modal is closed
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 100);
  };

  const handleFormSubmit = async (event: Omit<Event, 'id' | 'created_at' | 'updated_at'>) => {
    await onAddEvent(event);
    setShowAddForm(false);
  };

  return (
    <div className="events-list">
      <div className="events-list-header">
        <h3>All Events ({sortedEvents.length})</h3>
        <button 
          className="btn-add-event"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Event'}
        </button>
      </div>

      {showAddForm && (
        <div className="add-event-section">
          <EventForm
            onSubmit={handleFormSubmit}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {sortedEvents.length === 0 ? (
        <div className="empty-state">
          <p>No events yet. Add your first event above.</p>
        </div>
      ) : (
        <div className="events-table-container">
          <table className="events-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Title</th>
                <th>Location</th>
                <th>Priority</th>
                <th>Owner</th>
                <th>Partnership Angle</th>
                <th>Notes</th>
                <th className="actions-header"></th>
              </tr>
            </thead>
            <tbody>
              {sortedEvents.map(event => (
                <tr 
                  key={event.id} 
                  className="event-row"
                  onClick={() => handleRowClick(event)}
                >
                  <td className="date-cell">
                    <div className="date-primary">{formatDateForDisplay(event.start_date)}</div>
                    {event.end_date && event.end_date !== event.start_date && (
                      <div className="date-secondary">- {formatDateForDisplay(event.end_date)}</div>
                    )}
                  </td>
                  <td className="title-cell">
                    <div className="title-text">{event.title}</div>
                    {event.description && (
                      <div className="title-description">{event.description}</div>
                    )}
                  </td>
                  <td className="location-cell">{event.location || '—'}</td>
                  <td className="priority-cell">{getPriorityBadge(event.priority)}</td>
                  <td className="owner-cell">{event.owner || '—'}</td>
                  <td className="partnership-cell">
                    {event.partnership_angle ? (
                      <div className="partnership-text">{event.partnership_angle}</div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="notes-cell">
                    {event.notes ? (
                      <div className="notes-text">{event.notes}</div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="btn-icon-edit"
                      onClick={(e) => handleEditClick(e, event)}
                      aria-label="Edit event"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {selectedEvent && createPortal(
        <EventDetail
          event={selectedEvent}
          onUpdateEvent={onUpdateEvent}
          onDeleteEvent={onDeleteEvent}
          onClose={handleCloseModal}
          initialIsEditing={startInEditMode}
        />,
        document.body
      )}
    </div>
  );
};

export default EventsList;

